import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";
import PhotoUpload from "@/components/PhotoUpload";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", city: "", short_name: "", logo: "", primary_color: "#FF5500", secondary_color: "#FFB800", venue: "", staff: [] };

const STAFF_ROLES = ["Owner", "Head Coach", "Assistant Coach", "Coach"];

export default function TeamsTab() {
    const [teams, setTeams] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (t) => {
        setEditing(t);
        setForm({
            name: t.name, city: t.city || "", short_name: t.short_name,
            logo: t.logo || "",
            primary_color: t.primary_color, secondary_color: t.secondary_color,
            venue: t.venue || "",
            staff: (t.staff || []).map((s) => ({ name: s.name, role: s.role, photo_url: s.photo_url || "" })),
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { ...form, staff: form.staff.filter((s) => s.name.trim()) };
        try {
            if (editing) await api.put(`/teams/${editing.id}`, payload);
            else await api.post("/teams", payload);
            toast.success(editing ? "Team updated" : "Team created");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete ${t.name}? Its players and games will also be removed.`)) return;
        try {
            await api.delete(`/teams/${t.id}`);
            toast.success("Team deleted");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const field = (key, label, props = {}) => (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
            <Input
                required={key === "name" || key === "short_name"}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                data-testid={`team-form-${key}`}
                {...props}
            />
        </div>
    );

    return (
        <div data-testid="admin-teams-tab">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-bold uppercase text-xl text-white">Franchises ({teams.length})</h2>
                <button
                    data-testid="add-team-button"
                    onClick={openCreate}
                    className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                    <Plus className="w-4 h-4" /> New Team
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((t) => (
                    <div key={t.id} data-testid={`admin-team-card-${t.id}`} className="card-arena rounded-2xl p-5">
                        <div className="flex items-center gap-4">
                            <TeamCrest team={t} size="md" />
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold uppercase text-white truncate">{t.name}</p>
                                <p className="text-xs text-slate-500">
                                    {t.city} • {t.roster_count} players
                                </p>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    data-testid={`edit-team-${t.id}`}
                                    onClick={() => openEdit(t)}
                                    className="p-2 text-slate-400 hover:text-[#FFB800] transition-colors duration-200"
                                    aria-label={`Edit ${t.name}`}
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                    data-testid={`delete-team-${t.id}`}
                                    onClick={() => remove(t)}
                                    className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                    aria-label={`Delete ${t.name}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            {editing ? "Edit Team" : "New Team"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Create or edit a franchise with colors, coach and home venue.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} data-testid="team-form" className="space-y-4 mt-2">
                        {field("name", "Team Name", { placeholder: "YKP Dragons" })}
                        <div className="grid grid-cols-2 gap-4">
                            {field("short_name", "Code (3 letters)", { maxLength: 4, placeholder: "DRG" })}
                            {field("city", "City", { placeholder: "YKP City" })}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Primary Color</Label>
                                <input
                                    type="color"
                                    data-testid="team-form-primary-color"
                                    value={form.primary_color}
                                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                                    className="mt-2 w-full h-10 rounded-lg bg-[#08080C] border border-[#262638] cursor-pointer"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Secondary Color</Label>
                                <input
                                    type="color"
                                    data-testid="team-form-secondary-color"
                                    value={form.secondary_color}
                                    onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                                    className="mt-2 w-full h-10 rounded-lg bg-[#08080C] border border-[#262638] cursor-pointer"
                                />
                            </div>
                        </div>
                        {field("venue", "Home Venue")}
                        <PhotoUpload
                            label="Team Logo"
                            testid="team-form-logo"
                            value={form.logo}
                            onChange={(url) => setForm({ ...form, logo: url })}
                        />
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Staff — Coaches & Owner</Label>
                            {form.staff.map((s, i) => (
                                <div key={i} data-testid={`staff-row-${i}`} className="flex items-center gap-2 mt-3">
                                    <PhotoUpload
                                        compact
                                        testid={`staff-photo-${i}`}
                                        value={s.photo_url}
                                        onChange={(url) => setForm({ ...form, staff: form.staff.map((x, j) => (j === i ? { ...x, photo_url: url } : x)) })}
                                    />
                                    <Input
                                        data-testid={`staff-name-${i}`}
                                        value={s.name}
                                        onChange={(e) => setForm({ ...form, staff: form.staff.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                                        placeholder="Name"
                                        className="flex-1 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                    />
                                    <Select value={s.role} onValueChange={(v) => setForm({ ...form, staff: form.staff.map((x, j) => (j === i ? { ...x, role: v } : x)) })}>
                                        <SelectTrigger data-testid={`staff-role-${i}`} className="w-36 bg-[#08080C] border-[#262638] text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                            {STAFF_ROLES.map((r) => (
                                                <SelectItem key={r} value={r}>{r}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <button
                                        type="button"
                                        data-testid={`staff-remove-${i}`}
                                        onClick={() => setForm({ ...form, staff: form.staff.filter((_, j) => j !== i) })}
                                        className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                        aria-label="Remove staff member"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                data-testid="staff-add-button"
                                onClick={() => setForm({ ...form, staff: [...form.staff, { name: "", role: "Coach", photo_url: "" }] })}
                                className="mt-3 flex items-center gap-2 border border-dashed border-[#262638] hover:border-[#FF5500] hover:text-[#FF5500] text-slate-400 font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full transition-colors duration-200"
                            >
                                <Plus className="w-4 h-4" /> Add Staff
                            </button>
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                            <TeamCrest team={{ ...form, name: form.name || "NEW" }} size="md" testid="team-form-crest-preview" />
                            <span className="text-xs text-slate-500">Live crest preview</span>
                        </div>
                        <button
                            type="submit"
                            data-testid="team-form-submit"
                            disabled={saving}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? "Save Changes" : "Create Team"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
